import { z } from 'zod';

// Paginated Response schema (generic)
export const paginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().nonnegative(),
    page_size: z.number().int().positive(),
  });

// User settings schema
export const userSettingsSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']),
  language: z.string(),
  notifications: z.object({
    email: z.boolean(),
    push: z.boolean(),
    desktop: z.boolean(),
  }),
  ai: z.object({
    defaultModel: z.string(),
    temperature: z.number().min(0).max(2),
    maxTokens: z.number().int().positive(),
  }),
});
export type UserSettings = z.infer<typeof userSettingsSchema>;

// Uploaded File schema
export const uploadedFileSchema = z.object({
  id: z.string(),
  original_name: z.string(),
  filename: z.string(),
  file_path: z.string(),
  file_type: z.string(),
  mime_type: z.string(),
  size_bytes: z.number().int().nonnegative(),
  content_text: z.string().nullable(),
  meta_data: z.any().nullable(),
  status: z.string(),
  error_message: z.string().nullable(),
  is_public: z.boolean(),
  owner_id: z.number(),
  download_count: z.number().int().nonnegative(),
  reference_count: z.number().int().nonnegative(),
  embedding_id: z.string().nullable(),
  is_indexed: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  expires_at: z.string().nullable(),
});
export type UploadedFile = z.infer<typeof uploadedFileSchema>;

// File Upload Response schema
export const fileUploadResponseSchema = z.object({
  file: uploadedFileSchema,
  upload_id: z.string(),
});
export type FileUploadResponse = z.infer<typeof fileUploadResponseSchema>;

// File Upload Progress schema
export const fileUploadProgressSchema = z.object({
  progress: z.number().min(0).max(100),
  loaded: z.number().int().nonnegative(),
  total: z.number().int().positive(),
});
export type FileUploadProgress = z.infer<typeof fileUploadProgressSchema>;

// Additional types for compatibility (not Zod schemas)
export type PaginatedResponse<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};
